<template>
  <div
    class="gap-3 bg-primary pl-3 pr-4 pt-0.5 pb-0.5 cursor-pointer shadow-xs rounded-md"
  >
    <div class="flex items-center" @click="toggleImageVisibility">
      <div class="flex items-center grow">
        <div class="border-2 border-quaternary rounded-lg p-0.5">
          <Primitive as="as" :class="cn(variants({ size }), props.class)">
            <img
              :alt="label"
              v-if="imageUrl"
              :src="imageUrl"
              :class="cn(variants({ size }), props.class)"
            />
            <Icon v-else name="qr-code" variant="fill" size="full" />
          </Primitive>
        </div>
        <div class="pr-2 ml-3 py-1.5 flex justify-between grow h-full">
          <div class="flex flex-col justify-center">
            <div class="text-sm font-medium text-secondary">
              {{ label }}
            </div>
            <div class="text-xs font-regular text-tertiary">
              {{ $t("components.passport.qr_codes.linked") }}
              {{
                new Intl.DateTimeFormat("en-GB", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                }).format(new Date(props.qrCode.updated_at))
              }}
            </div>
          </div>
          <div v-if="canUpdate" class="flex items-center">
            <Tooltip :title="$t('components.passport.qr_codes.delete_code')">
              <IconButton
                variant="tertiary"
                type="gray"
                size="xl"
                @click.stop="askRemovalConfirmation(qrCode.id)"
              >
                <Icon name="delete" size="lg" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center grow" v-if="showImage && imageUrl">
      <img :alt="label" :src="imageUrl" class="full" />
    </div>
  </div>
  <DeleteModal
    v-if="canUpdate"
    :open="!!qrCodeBeingRemoved"
    :description="$t('admin.unassign_qr_code.dialog.message')"
    @cancel="qrCodeBeingRemoved = null"
    @delete="handleRemovalConfirmed"
    :title="$t('admin.unassign_qr_code.dialog.title')"
  ></DeleteModal>
</template>

<script setup>
import { ref } from "vue";
import { Primitive } from "radix-vue";
import { IconButton } from "@/SharedComponents/Buttons";
import { Icon, variants } from "@/SharedComponents/Icon";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { sizes } from "@/SharedComponents/Indicator";
import { cn } from "@/Utils/cn";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { DeleteModal } from "@/SharedComponents/Modals";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  qrCode: { type: Object, required: true },
  label: { type: String, required: false },
  canUpdate: { type: Boolean, required: true },
  isGroup: {
    type: Boolean,
    default: false,
  },
  size: {
    type: null,
    validator: (val) => sizes.includes(val),
    required: false,
    default: "3xl",
  },
  as: {
    type: String,
    default: "div",
  },
  imageUrl: {
    type: String,
    default: null,
  },
  class: {
    type: String,
    required: false,
    default: "3xl rounded-md",
  },
});

// Refs
const qrCodeBeingRemoved = ref(null);
const showImage = ref(false);

const emit = defineEmits(["update:item"]);

const toggleImageVisibility = () => {
  showImage.value = !showImage.value;
};

// Functions
const askRemovalConfirmation = (id) => {
  qrCodeBeingRemoved.value = id;
};

const handleRemovalConfirmed = () => {
  router.delete(route("qr_code.unassign", qrCodeBeingRemoved.value), {
    onSuccess: (response) => {
      toast.success(t("toasts.qr_code.unassign_space.success"));

      if (response.props.asset) {
        emit("update:item", {
          item: {
            ...response.props.asset,
          },
        });
      }

      if (response.props.space) {
        emit("update:item", {
          item: {
            ...response.props.space,
          },
        });
      }
    },
    onError: () => {
      toast.error(t("toasts.error"));
    },
    onFinish: () => {
      qrCodeBeingRemoved.value = null;
    },
  });
};
</script>
